import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

// ─── Column Config Types ──────────────────────────────────────────────────────

export interface TrackingReportColumnConfigItem {
  key: string;
  label: string;
  order: number;
  visible?: boolean;
}

export interface TrackingReportTableConfigPayload {
  name?: string;
  fieldConfigJson?: {
    data: TrackingReportColumnConfigItem[];
  };
  isDefault?: boolean;
}

export interface TrackingReportTableConfig {
  id?: number;
  name?: string;
  configName?: string;
  fieldConfigJson?: {
    data: TrackingReportColumnConfigItem[];
  };
  data?: TrackingReportColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

// ─── Row Type ────────────────────────────────────────────────────────────────

export type SalesTrackingReportingRow = {
  salesOrder: {
    id: number;
    requestNo: string | null;
    sapOrderNumber: string | null;
    approvalStatus: string | null;
    isFinalApproved: boolean;
    finalApprovedTimeStamp?: string | null;
    customer?: {
      id: number;
      name: string;
      code?: string | null;
      erpCode?: string | null;
    } | null;
    company?: any;
    companyGroup?: any;
    plant?: any;
    salesOrderType?: any;
    currency?: any;
    [key: string]: any;
  };
  lineItem: {
    id: number;
    salesOrderId: number;
    itemNo: number | null;
    materialId: number | null;
    qty: string | number | null;
    ratePerUnit?: string | number | null;
    lineGrossValue?: string | number | null;
    lineNetValue?: string | number | null;
    material?: {
      ID: number;
      MaterialCode?: string | null;
      Description?: string | null;
      productType?: any;
      productGroup?: any;
      [key: string]: any;
    } | null;
    materialGroup1?: any;
    materialGroup2?: any;
    materialGroup3?: any;
    uom?: any;
    [key: string]: any;
  };
  billingDocumentItems: Array<{
    billingDocument: string;
    billingDocumentItem: string;
    salesDocument: string | null;
    salesDocumentItem: string | null;
    billingQuantityInBaseUnit?: string | null;
    header?: {
      billingDocumentType?: string | null;
      billingDocumentDate?: string | null;
    } | null;
  }>;
  purchaseRequisitionItems: Array<{
    purchaseRequisition: string;
    purchaseRequisitionItem: string;
    requirementTracking: string | null;
    purchasingDocument: string | null;
    purchasingDocumentItem: string | null;
  }>;
  purchaseOrderItems: Array<{
    purchaseOrder: string;
    purchaseOrderItem: string;
    purchaseRequisition?: string | null;
    purchaseRequisitionItem?: string | null;
    orderQuantity?: string | null;
    netAmount?: string | null;
  }>;
  purchaseOrderHeaders: Array<{
    purchaseOrder: string;
    purchaseOrderType?: string | null;
    purchaseOrderDate?: string | null;
    supplier?: string | null;
    documentCurrency?: string | null;
  }>;
};

export interface TrackingReportMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TrackingReportResponse {
  message?: string;
  data: SalesTrackingReportingRow[];
  meta: TrackingReportMeta;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function parseTrackingReportFieldConfigJson(
  fieldConfigJson: unknown,
): { data: TrackingReportColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed as TrackingReportColumnConfigItem[] };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return parsed as { data: TrackingReportColumnConfigItem[] };
  }

  return undefined;
}

export function extractTrackingReportActiveConfigId(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const record = raw as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;
  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }
  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizeTrackingReportTableConfig(
  raw: unknown,
): TrackingReportTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as TrackingReportTableConfig)
      : (raw as TrackingReportTableConfig);

  const fieldConfigJson =
    parseTrackingReportFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractTrackingReportActiveConfigId(raw);
    if (id) return { ...nested, id };
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
    data: nested.data ?? fieldConfigJson.data,
  };
}

export function getTrackingReportConfigColumns(
  config?: TrackingReportTableConfig | null,
): TrackingReportColumnConfigItem[] {
  if (!config) return [];

  const fieldConfig =
    parseTrackingReportFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(column => column.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// ─── RTK Query API ────────────────────────────────────────────────────────────

const salesTrackingReportingApi = createApi({
  reducerPath: 'salesTrackingReporting',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SalesTrackingReport', 'TrackingReportTableConfig'],
  endpoints: builder => ({
    // Main data endpoint
    getSalesTrackingReport: builder.query<TrackingReportResponse, any>({
      query: params => ({
        url: `/sales-tracking-reporting${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesTrackingReport'],
    }),

    // Export endpoint
    exportSalesTrackingReport: builder.query<Blob, any>({
      query: params => ({
        url: `/sales-tracking-reporting/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Table config list
    getTrackingReportTableConfigList: builder.query<
      BackendResponse<TrackingReportTableConfig[]>,
      void
    >({
      query: () => ({
        url: '/sales-tracking-reporting-table-config',
        method: 'GET',
      }),
      providesTags: ['TrackingReportTableConfig'],
    }),

    // Active table config
    getActiveTrackingReportTableConfig: builder.query<
      BackendResponse<TrackingReportTableConfig>,
      void
    >({
      query: () => ({
        url: '/sales-tracking-reporting-table-config/active',
        method: 'GET',
      }),
      providesTags: ['TrackingReportTableConfig'],
    }),

    // Get config by ID
    getTrackingReportTableConfig: builder.query<
      BackendResponse<TrackingReportTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-tracking-reporting-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'TrackingReportTableConfig', id }],
    }),

    // Create config
    createTrackingReportTableConfig: builder.mutation<
      BackendResponse<TrackingReportTableConfig>,
      Required<Pick<TrackingReportTableConfigPayload, 'name' | 'fieldConfigJson'>> &
        Pick<TrackingReportTableConfigPayload, 'isDefault'>
    >({
      query: payload => ({
        url: '/sales-tracking-reporting-table-config',
        method: 'POST',
        body: payload,
      }),
    }),

    // Update config
    updateTrackingReportTableConfig: builder.mutation<
      BackendResponse<TrackingReportTableConfig>,
      { id: number; data: TrackingReportTableConfigPayload }
    >({
      query: ({ id, data }) => ({
        url: `/sales-tracking-reporting-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // Delete config
    deleteTrackingReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-tracking-reporting-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TrackingReportTableConfig'],
    }),

    // Apply config
    applyTrackingReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { configId: number }
    >({
      query: payload => ({
        url: '/sales-tracking-reporting-table-config/apply',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TrackingReportTableConfig'],
    }),
  }),
});

export const {
  useGetSalesTrackingReportQuery,
  useLazyExportSalesTrackingReportQuery,
  useGetTrackingReportTableConfigListQuery,
  useGetActiveTrackingReportTableConfigQuery,
  useGetTrackingReportTableConfigQuery,
  useLazyGetTrackingReportTableConfigQuery,
  useCreateTrackingReportTableConfigMutation,
  useUpdateTrackingReportTableConfigMutation,
  useDeleteTrackingReportTableConfigMutation,
  useApplyTrackingReportTableConfigMutation,
} = salesTrackingReportingApi;

export default salesTrackingReportingApi;
